import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react';

const ADMIN_EMAIL = 'engineercse3@gmail.com';

export default function Login() {
  const [mode, setMode] = useState('password');
  const [email, setEmail] = useState(ADMIN_EMAIL);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, sendOTP, verifyOTP, forgotPassword, resetPasswordWithOTP } = useAuth();
  const navigate = useNavigate();

  const resetFeedback = () => {
    setError('');
    setMessage('');
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    resetFeedback();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLoginOTP = async () => {
    resetFeedback();
    setLoading(true);
    try {
      const res = await sendOTP(email);
      setMessage(res.message || 'OTP sent to your email');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLoginOTP = async (e) => {
    e.preventDefault();
    resetFeedback();
    setLoading(true);
    try {
      await verifyOTP(email, otp);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    resetFeedback();
    setLoading(true);
    try {
      const res = await forgotPassword(email);
      setMessage(res.message || 'Password reset OTP sent to your email');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send password reset OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    resetFeedback();
    setLoading(true);
    try {
      const res = await resetPasswordWithOTP(email, otp, newPassword);
      setMessage(res.message || 'Password reset successful. Please sign in.');
      setMode('password');
      setPassword('');
      setNewPassword('');
      setOtp('');
      setShowNewPassword(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setOtp('');
    setPassword('');
    setNewPassword('');
    setShowPassword(false);
    setShowNewPassword(false);
    resetFeedback();
  };

  const renderEmailField = () => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
          placeholder={ADMIN_EMAIL}
          required
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-cyan-800 to-blue-700 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-cyan-100 text-cyan-700 flex items-center justify-center mb-4">
            <ShieldCheck size={28} />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Algoryn Admin</h1>
          
        </div>

        <div className="grid grid-cols-3 gap-2 p-1 bg-gray-100 rounded-xl mb-6">
          <button type="button" onClick={() => switchMode('password')} className={`py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'password' ? 'bg-white text-cyan-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            Password
          </button>
          <button type="button" onClick={() => switchMode('otp')} className={`py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'otp' ? 'bg-white text-cyan-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            Login OTP
          </button>
          <button type="button" onClick={() => switchMode('forgot')} className={`py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'forgot' ? 'bg-white text-cyan-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            Forgot
          </button>
        </div>

        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
        {message && <div className="bg-emerald-50 text-emerald-700 p-3 rounded-lg mb-4 text-sm">{message}</div>}

        {mode === 'password' && (
          <form onSubmit={handlePasswordLogin} className="space-y-5">
            {renderEmailField()}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  placeholder="Enter password"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full py-3 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {mode === 'otp' && (
          <form onSubmit={handleVerifyLoginOTP} className="space-y-5">
            {renderEmailField()}

            <div className="grid grid-cols-[1fr_auto] gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Login OTP</label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                    placeholder="6-digit OTP"
                    required
                  />
                </div>
              </div>
              <button type="button" onClick={handleSendLoginOTP} disabled={loading} className="self-end px-4 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-50">
                Send OTP
              </button>
            </div>

            <button type="submit" disabled={loading} className="w-full py-3 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50">
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleResetPassword} className="space-y-5">
            {renderEmailField()}

            <button type="button" onClick={handleForgotPassword} disabled={loading} className="w-full py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-50">
              {loading ? 'Sending OTP...' : 'Send Reset OTP'}
            </button>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reset OTP</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  placeholder="Enter OTP"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  placeholder="Minimum 6 characters"
                  required
                />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full py-3 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
